import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { uploadVideo, uploadMultipleVideos } from '../../utils/videoUploadManager';
type MediaType = 'image' | 'video';
type MediaFile = File & { preview?: string, type: string };

const Container = styled.div`
  padding: 20px;
  animation: fadeIn 0.5s ease;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  
  [data-theme='dark'] & {
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  }
`;

const Title = styled.h1`
  margin: 0;
  color: var(--text-primary);
  font-size: 1.8rem;
  font-weight: 600;
`;

const UploadContainer = styled.div`
  background-color: var(--paper-color);
  border-radius: var(--border-radius);
  padding: 30px;
  box-shadow: var(--box-shadow);
  margin-bottom: 30px;
  transition: all 0.3s ease;
  animation: slideUp 0.5s ease;
  
  &:hover {
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08);
  }
`;

const VisitorSelector = styled.div`
  margin-bottom: 25px;
`;

const TabContainer = styled.div`
  display: flex;
  margin-bottom: 25px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  
  [data-theme='dark'] & {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 12px 20px;
  background: ${props => props.active ? 'var(--primary-color)' : 'transparent'};
  color: ${props => props.active ? 'white' : 'var(--text-secondary)'};
  border: none;
  border-radius: 8px 8px 0 0;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Poppins', sans-serif;
  display: flex;
  align-items: center;
  
  &:hover {
    background: ${props => props.active ? 'var(--primary-color)' : 'rgba(67, 97, 238, 0.1)'};
    transform: translateY(0);
  }
  
  svg {
    margin-right: 8px;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 10px;
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.95rem;
`;

const Select = styled.select`
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #e1e1e1;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.3s;
  background-color: #f9f9f9;
  color: var(--text-primary);
  font-family: 'Poppins', sans-serif;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 15px center;
  background-size: 15px;
  padding-right: 40px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background-color: white;
    box-shadow: 0 0 0 4px rgba(67, 97, 238, 0.1);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  [data-theme='dark'] & {
    background-color: #2d2d2d;
    border-color: #444;
    color: var(--text-primary);
    
    &:focus {
      background-color: #333;
      border-color: var(--primary-color);
      box-shadow: 0 0 0 4px rgba(67, 97, 238, 0.2);
    }
  }
`;

const DropZone = styled.div<{ isDragActive: boolean; hasFiles: boolean }>`
  border: 2px dashed ${(props) => (props.isDragActive ? 'var(--primary-color)' : props.hasFiles ? 'var(--primary-light)' : '#ccc')};
  border-radius: 12px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  margin-bottom: 25px;
  background-color: ${(props) => (props.isDragActive ? 'rgba(67, 97, 238, 0.05)' : props.hasFiles ? 'rgba(67, 97, 238, 0.02)' : 'transparent')};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: var(--primary-color);
    background-color: rgba(67, 97, 238, 0.05);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: ${props => props.hasFiles ? '5px' : '0'};
    background: linear-gradient(90deg, var(--primary-color), var(--primary-light));
    transition: height 0.3s ease;
  }
  
  [data-theme='dark'] & {
    border-color: ${(props) => (props.isDragActive ? 'var(--primary-color)' : props.hasFiles ? 'var(--primary-light)' : '#444')};
    background-color: ${(props) => (props.isDragActive ? 'rgba(67, 97, 238, 0.1)' : props.hasFiles ? 'rgba(67, 97, 238, 0.05)' : 'transparent')};
  }
`;

const DropZoneIcon = styled.div<{ $isDragActive?: boolean }>`
  margin-bottom: 15px;
  
  svg {
    width: 60px;
    height: 60px;
    color: ${props => props.$isDragActive ? 'var(--primary-color)' : '#aaa'};
    transition: color 0.3s ease;
  }
`;

const DropZoneText = styled.p`
  margin: 0;
  color: var(--text-secondary);
  font-size: 16px;
  font-weight: 500;
`;

const DropZoneHint = styled.p`
  margin: 5px 0 0;
  color: var(--text-light);
  font-size: 14px;
`;

const FilesPreview = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 15px;
  margin-top: 25px;
`;

const FilePreview = styled.div`
  position: relative;
  width: 100%;
  padding-top: 100%;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
  
  [data-theme='dark'] & {
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.3);
  }
`;

const VideoPreview = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #000;
  
  &::before {
    content: '\25B6';
    color: rgba(255, 255, 255, 0.7);
    font-size: 24px;
    position: absolute;
    z-index: 2;
  }
  
  &::after {
    content: 'VIDEO';
    position: absolute;
    bottom: 8px;
    right: 8px;
    background-color: rgba(0, 0, 0, 0.7);
    color: white;
    font-size: 10px;
    padding: 3px 6px;
    border-radius: 4px;
  }
`;

const PreviewImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const RemoveFileButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: rgba(239, 71, 111, 0.9);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const UploadButton = styled.button`
  background: linear-gradient(to right, var(--primary-color), var(--primary-dark));
  color: white;
  border: none;
  border-radius: 8px;
  padding: 14px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 8px rgba(67, 97, 238, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 12px rgba(67, 97, 238, 0.3);
  }

  &:active {
    transform: translateY(-1px);
  }

  &:disabled {
    background: #bdbdbd;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  svg {
    margin-right: 8px;
  }
`;

const ProgressContainer = styled.div`
  margin-top: 25px;
`;

const ProgressBar = styled.div<{ progress: number }>`
  height: 8px;
  background-color: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 10px;
  position: relative;

  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${(props) => props.progress}%;
    background: linear-gradient(to right, var(--primary-color), var(--primary-light));
    border-radius: 4px;
    transition: width 0.3s ease;
  }
`;

const ProgressText = styled.p`
  margin: 0;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  margin-top: 20px;
  padding: 12px 16px;
  background-color: rgba(239, 71, 111, 0.1);
  border-radius: 8px;
  font-weight: 500;
  animation: fadeIn 0.3s ease;
`;

const SuccessMessage = styled.div`
  color: var(--success-color);
  margin-top: 20px;
  padding: 12px 16px;
  background-color: rgba(6, 214, 160, 0.1);
  border-radius: 8px;
  font-weight: 500;
  animation: fadeIn 0.3s ease;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 10px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid rgba(67, 97, 238, 0.2);
  border-radius: 50%;
  border-top-color: var(--primary-color);
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

interface Visitor {
  id: string;
  token: string;
  visitorName: string;
}

// Icon components
const UploadIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4C9.11 4 6.6 5.64 5.35 8.04C2.34 8.36 0 10.91 0 14C0 17.31 2.69 20 6 20H19C21.76 20 24 17.76 24 15C24 12.36 21.95 10.22 19.35 10.04ZM14 13V17H10V13H7L12 8L17 13H14Z" fill="currentColor"/>
  </svg>
);

const DropIcon = () => (
  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4C9.11 4 6.6 5.64 5.35 8.04C2.34 8.36 0 10.91 0 14C0 17.31 2.69 20 6 20H19C21.76 20 24 17.76 24 15C24 12.36 21.95 10.22 19.35 10.04ZM19 18H6C3.79 18 2 16.21 2 14C2 11.95 3.53 10.24 5.56 10.03L6.63 9.92L7.13 8.97C8.08 7.14 9.94 6 12 6C14.62 6 16.88 7.86 17.39 10.43L17.69 11.93L19.22 12.04C20.78 12.14 22 13.45 22 15C22 16.65 20.65 18 19 18ZM8 13H10.55V16H13.45V13H16L12 9L8 13Z" fill="currentColor"/>
  </svg>
);

const SuccessIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM16.59 7.58L10 14.17L7.41 11.59L6 13L10 17L18 9L16.59 7.58Z" fill="currentColor"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
  </svg>
);

const ImageTabIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.5L14.5 12L19 18H5L8.5 13.5Z" fill="currentColor"/>
  </svg>
);

const VideoTabIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 10.5V7C17 6.45 16.55 6 16 6H4C3.45 6 3 6.45 3 7V17C3 17.55 3.45 18 4 18H16C16.55 18 17 17.55 17 17V13.5L21 17.5V6.5L17 10.5Z" fill="currentColor"/>
  </svg>
);

const UploadImages: React.FC = () => {
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [selectedVisitor, setSelectedVisitor] = useState<string>('');
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [mediaType, setMediaType] = useState<MediaType>('image');
  const [isDragActive, setIsDragActive] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const { token } = useAuth();

  useEffect(() => {
    // Fetch visitors
    const fetchVisitors = async () => {
      try {
        console.log('Fetching visitors for upload component');
        setLoading(true);
        
        // Set a high limit to get all visitors at once
        const response = await api.get('/qrcodes', {
          params: {
            limit: 1000, // Much higher than expected number of visitors
            page: 1
          }
        });
        
        // The API returns {visitors: [...], totalPages: number, currentPage: number, totalVisitors: number}
        if (response.data && response.data.visitors && Array.isArray(response.data.visitors)) {
          console.log('Visitors loaded:', response.data.visitors.length);
          setVisitors(response.data.visitors);
        } else {
          // Handle the case where the response is not what we expect
          console.error('Unexpected API response format:', response.data);
          setVisitors([]);
          setError('Failed to load visitors: Unexpected data format');
        }
      } catch (err) {
        console.error('Error fetching visitors:', err);
        setError('Failed to load visitors. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };

    fetchVisitors();
  }, []);

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (fileList: FileList) => {
    const supportedFiles = Array.from(fileList).filter(file => {
      if (mediaType === 'image') {
        return file.type.startsWith('image/');
      } else {
        return file.type.startsWith('video/');
      }
    }) as MediaFile[];
    
    setFiles(prevFiles => [...prevFiles, ...supportedFiles]);
  };

  const removeFile = (index: number) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (!selectedVisitor || files.length === 0) {
      setError(`Please select a visitor and at least one ${mediaType}.`);
      return;
    }

    setError('');
    setSuccessMessage('');
    setUploading(true);
    setUploadProgress(0);

    try {
      // Separate images and videos for different upload methods
      const imageFiles = files.filter(file => file.type.startsWith('image/'));
      const videoFiles = files.filter(file => file.type.startsWith('video/'));
      
      // Process regular image uploads if any
      if (imageFiles.length > 0 && mediaType === 'image') {
        const formData = new FormData();
        formData.append('visitorId', selectedVisitor);
        formData.append('mediaType', 'image');
        
        imageFiles.forEach(file => {
          formData.append('media', file);
        });

        await api.post(`/media/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(percentCompleted);
            }
          }
        });
      }
      
      // Process video uploads with automatic chunking for large files
      if (videoFiles.length > 0 && mediaType === 'video') {
        // For multiple videos, use the batch uploader
        await uploadMultipleVideos({
          files: videoFiles,
          visitorId: selectedVisitor,
          token: token,  // This is explicit to show we're passing the token
          onTotalProgress: (progress) => {
            setUploadProgress(progress);
          },
          onError: (error) => {
            console.error('Error uploading video:', error);
            // Continue with other files
          }
        });
      }

      console.log('Upload successful');
      setSuccessMessage(`Successfully uploaded ${files.length} ${mediaType}${files.length > 1 ? 's' : ''}.`);
      setFiles([]);
      setUploadProgress(0);
    } catch (err) {
      console.error(`Error uploading ${mediaType}s:`, err);
      setError(`Failed to upload ${mediaType}s. Please try again.`);
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Header>
          <Title>Upload Media</Title>
        </Header>
        <LoadingContainer>
          <LoadingSpinner />
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Upload Media</Title>
      </Header>

      <UploadContainer>
        <VisitorSelector>
          <Label htmlFor="visitor-select">Select Visitor</Label>
          <Select 
            id="visitor-select"
            value={selectedVisitor}
            onChange={(e) => setSelectedVisitor(e.target.value)}
            disabled={uploading}
          >
            <option value="">-- Select a visitor --</option>
            {visitors.map((visitor) => (
              <option key={visitor.id} value={visitor.id}>
                {visitor.visitorName}
              </option>
            ))}
          </Select>
        </VisitorSelector>

        <TabContainer>
          <Tab 
            active={mediaType === 'image'} 
            onClick={() => {
              setMediaType('image');
              setFiles([]);
            }}
          >
            <ImageTabIcon />
            Images
          </Tab>
          <Tab 
            active={mediaType === 'video'} 
            onClick={() => {
              setMediaType('video');
              setFiles([]);
            }}
          >
            <VideoTabIcon />
            Videos
          </Tab>
        </TabContainer>

        <Label>Upload {mediaType === 'image' ? 'Images' : 'Videos'}</Label>
        <DropZone
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          isDragActive={isDragActive}
          hasFiles={files.length > 0}
          onClick={() => document.getElementById('file-input')?.click()}
        >
          <input
            id="file-input"
            type="file"
            multiple
            accept={mediaType === 'image' ? 'image/*' : 'video/*'}
            onChange={handleFileInput}
            style={{ display: 'none' }}
            disabled={uploading}
          />
          <DropZoneIcon $isDragActive={isDragActive}>
            <DropIcon />
          </DropZoneIcon>
          <DropZoneText>
            {isDragActive
              ? `Drop ${mediaType}s here`
              : `Click or drag & drop ${mediaType}s here to upload`}
          </DropZoneText>
          <DropZoneHint>
            {mediaType === 'image'
              ? 'Supports: JPEG, PNG, GIF (up to 25MB each)'
              : 'Supports: MP4, WebM, MOV (up to 100MB each)'}
          </DropZoneHint>

          {files.length > 0 && (
            <FilesPreview>
              {files.map((file, index) => (
                <FilePreview key={index}>
                  {file.type.startsWith('image/') ? (
                    <PreviewImage src={URL.createObjectURL(file)} alt={`Preview ${index}`} />
                  ) : (
                    <VideoPreview />
                  )}
                  <RemoveFileButton 
                    type="button" 
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(index);
                    }}
                    disabled={uploading}
                  >
                    <CloseIcon />
                  </RemoveFileButton>
                </FilePreview>
              ))}
            </FilesPreview>
          )}
        </DropZone>

        <UploadButton 
          type="button" 
          onClick={handleUpload} 
          disabled={uploading || files.length === 0 || !selectedVisitor}
        >
          {uploading ? (
            <>
              <LoadingSpinner />
              Uploading...
            </>
          ) : (
            <>
              <UploadIcon />
              {`Upload ${files.length} ${mediaType === 'image' ? 'Image' : 'Video'}${files.length !== 1 ? 's' : ''}`}
            </>
          )}
        </UploadButton>

        {uploading && (
          <ProgressContainer>
            <ProgressBar progress={uploadProgress} />
            <ProgressText>{uploadProgress}% Uploaded</ProgressText>
          </ProgressContainer>
        )}

        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        {successMessage && (
          <SuccessMessage>
            <SuccessIcon />
            {successMessage}
          </SuccessMessage>
        )}
      </UploadContainer>
    </Container>
  );
};

export default UploadImages;
